import FormRange from 'react-bootstrap/FormRange';
import Dropdown from 'react-bootstrap/Dropdown';
import { Button } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { AppDispatch, RootState } from 'Vendor/Store/store';
import { useDispatch, useSelector } from 'react-redux';
import {
    responsiveModeToggled,
    zoomTo,
    gridVisibilityToggled,
    pageSwitched,
    pageAdded,
} from 'Vendor/Store/studio';
import { MAX_ZOOM, MIN_ZOOM, PRESET_ZOOMS } from 'Vendor/Utils/Constants';
import { selectIsGridVisible } from 'Vendor/Store/studio/selectors';

type DeviceView = 'mobile' | 'desktop';

const BottomControls = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { responsiveMode, zoomLevel, pages, pageIds, activePageId } =
        useSelector((state: RootState) => state.studio.present);
    const isGridVisible = useSelector(selectIsGridVisible);
    const [showPageDropdown, setShowPageDropdown] = useState(false);

    const handlePageDropdownToggle = (isOpen: boolean) => {
        setShowPageDropdown(isOpen);
    };

    const activePage = pages?.[activePageId];

    const handleViewChange = (view: DeviceView) => {
        if (responsiveMode !== view) {
            dispatch(responsiveModeToggled());
            if (view === 'mobile') {
                dispatch(zoomTo(1.2));
            } else {
                dispatch(zoomTo(1.0));
            }
        }
    };

    const handleGridToggle = () => {
        dispatch(gridVisibilityToggled());
    };

    const [zoomValue, setZoomValue] = useState(zoomLevel * 100);

    useEffect(() => {
        setZoomValue(zoomLevel * 100);
    }, [zoomLevel]);

    const handleResetZoom = () => {
        dispatch(zoomTo(1.0));
    };

    const handleZoomSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(zoomTo(Math.round(Number(e.target.value)) / 100));
    };

    const handlePresetZoomClick = (val: number) => {
        dispatch(zoomTo(val / 100));
    };

    const handleZoomDropdownClick = (e: React.MouseEvent) => {
        if (e.detail > 1) handleResetZoom();
    };

    const handlePageSwitch = (pageId: string) => {
        if (pageId !== activePageId) {
            dispatch(pageSwitched({ pageId }));
        }
    };

    const handlePageAdd = () => {
        dispatch(pageAdded());
    };

    if (!pages || !pageIds || !activePage) {
        return null;
    }

    return (
        <div className="w-100 bottom-controls position-absolute bottom-0 d-flex justify-content-between align-items-center p-4 bg-white">
            <div className="space-x-4 d-flex align-items-center">
                <div className="position-relative">
                    <Dropdown drop="up" show={showPageDropdown} onToggle={handlePageDropdownToggle}>
                        <Dropdown.Toggle
                            id="page-nav-dropdown"
                            variant="light"
                            className="text-truncate"
                            style={{ maxWidth: '150px' }}
                        >
                            {activePage.name}
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="dropdown-menu-up">
                            {pageIds.map((id) => (
                                <Dropdown.Item
                                    key={id}
                                    active={id === activePageId}
                                    onClick={() => handlePageSwitch(id)}
                                    className="text-truncate"
                                >
                                    {pages[id].name}
                                </Dropdown.Item>
                            ))}
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={handlePageAdd}>
                                <i className="fa-solid fa-plus me-2"></i>
                                Add New Page
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>

                <FormRange
                    min={MIN_ZOOM * 100}
                    max={MAX_ZOOM * 100}
                    value={zoomValue}
                    onChange={handleZoomSliderChange}
                    style={{ width: '100px' }}
                />
                <Dropdown>
                    <Dropdown.Toggle
                        id="zoom-level-dropdown"
                        onClick={handleZoomDropdownClick}
                    >
                        {Math.round(zoomValue)}%
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {PRESET_ZOOMS.map((val: number) => (
                            <Dropdown.Item
                                key={val}
                                onClick={() => handlePresetZoomClick(val)}
                            >
                                {val}%
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <div className="space-x-4 d-flex align-items-center">
                <Button
                    variant="light"
                    className={`${responsiveMode === 'desktop' ? 'active' : ''}`}
                    onClick={() => handleViewChange('desktop')}
                >
                    <i className="fa-solid fa-desktop fs-2"></i>
                </Button>

                <Button
                    variant="light"
                    className={`${responsiveMode === 'mobile' ? 'active' : ''}`}
                    onClick={() => handleViewChange('mobile')}
                >
                    <i className="fa-solid fa-mobile-screen-button fs-2"></i>
                </Button>
            </div>

            <div className="space-x-4 d-flex align-items-center">
                <Button
                    variant="link"
                    onClick={handleGridToggle}
                    className="p-0 text-decoration-none"
                >
                    <i
                        className={`fa-solid fa-border-all fs-2 ${isGridVisible ? 'text-primary' : 'text-muted'
                            }`}
                        title={isGridVisible ? 'Hide Grid' : 'Show Grid'}
                    />
                </Button>
                <i className="fa-solid fa-circle-question fs-2 text-muted"></i>
            </div>
        </div>
    );
};

export default BottomControls;
